import MetaTags from "../components/MetaTags";
import {
  Box,
  Flex,
  Heading,
  Text,
  SimpleGrid,
  Center,
  Stack,
  UnorderedList,
  ListItem,
  Link,
  Button,
  useColorModeValue,
  useColorMode,
  Grid,
  VStack,
  Icon,
  Container,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  FaRegHandshake,
  FaBalanceScale,
  FaBrain,
  FaBullseye,
  FaAward,
  FaLeaf,
  FaRocket,
  FaEye,
  FaCompass,
} from "react-icons/fa";

import Image from "next/image";
import Footer from "../components/footer";
import Chatbot from "./chatbot";
import CarbonEmissionCalculator from "./carbonEmission";

import AIImg from "../public/assets/AIImg-removebg-preview.png";
import ImageFlowchart from "../public/assets/AptofyFlowchart.png";
import bulb from "../public/assets/bulb.png";
import stopwatch from "../public/assets/stopwatch-removebg-preview.png";
import convoyerbelt from "../public/convoyerbelt.png";

import { useEffect, useState } from "react";
import Customerservicebot from "./customerservicesbot";

const AboutPage = () => {
  const bg = useColorModeValue("gray.100", "blue.800");
  const { colorMode } = useColorMode();
  
  //Store userType
  const [userType, setUserType] = useState(null);

  const MotionBox = motion(Box);
  const MotionHeading = motion(Heading);
  const MotionText = motion(Text);
  const MotionButton = motion(Button);

  //to get the user type so that we can display chat bot accordingly
  useEffect(() => {
    const role = sessionStorage.getItem("userType");
    setUserType(role);
  }, []);

  const values = [
    {
      title: "Integrity",
      desc: "We prioritize transparency, fairness, and ethical practices in every interaction.",
      icon: FaBalanceScale,
      color: "blue.500",
    },
    {
      title: "Responsibility",
      desc: "We are accountable to our clients, candidates, and partners, ensuring trust and reliability.",
      icon: FaRegHandshake,
      color: "green.500",
    },
    {
      title: "Competence",
      desc: "Our expertise and innovative approach drive consistent, high-quality solutions.",
      icon: FaBrain,
      color: "purple.500",
    },
    {
      title: "Value Centricity",
      desc: "We focus on delivering meaningful and measurable impact to every stakeholder we serve.",
      icon: FaBullseye,
      color: "orange.500",
    },
  ];

  const advantages = [
    {
      img: AIImg,
      title: "Cost-Effective Hiring Solution powered by AI",
      desc: "Revolutionize your hiring process with our AI-powered solution! Automate recruitment and assessments effortlessly, eliminating the need for expert intervention. Boost efficiency and cut costs with precision-driven automation.",
      gradient: "linear(135deg, #3B58D7, #5B73E8)",
      soft: "rgba(59,88,215,0.10)",
      accent: "#3B58D7",
    },
    {
      img: bulb,
      title: "Power Your Organization with Cutting-Edge Talent",
      desc: "Empower your organization to hire for cutting-edge tech roles with our advanced solutions. Ensure a cheat-proof test environment with our video and audio proctoring capabilities.",
      gradient: "linear(135deg, #7C5CE8, #5B73E8)",
      soft: "rgba(124,92,232,0.10)",
      accent: "#7C5CE8",
    },
    {
      img: stopwatch,
      title: "Empower Your Hiring Team with 24/7 AI Precision",
      desc: "Harness AI-powered technology for accurate, consistent, and bias-free evaluations, allowing your hiring team to focus on strategic tasks. Aptofy is available 24/7, ensuring swift closure of job requirements. Designed to empower, not replace, human recruiters.",
      gradient: "linear(135deg, #0BB5A3, #16C79A)",
      soft: "rgba(11,181,163,0.10)",
      accent: "#0BB5A3",
    },
    {
      img: convoyerbelt,
      title: "Build a Future-Ready Talent Pipeline",
      desc: "Build and maintain a talent pipeline of skilled professionals, ensuring a steady flow of qualified candidates for future hiring and sourcing needs.",
      gradient: "linear(135deg, #F6993F, #F2B05E)",
      soft: "rgba(246,153,63,0.10)",
      accent: "#F6993F",
    },
  ];

  // Shared style tokens for a cohesive, professional look
  const heroBg = useColorModeValue("#ffffff", "#1A202C");
  const heroGlow = "transparent";
  const cardBg = useColorModeValue("white", "gray.800");
  const cardBorder = useColorModeValue("gray.100", "gray.700");
  const mutedText = useColorModeValue("gray.600", "gray.300");
  const headingColor = useColorModeValue("#1A202C", "white");
  const eyebrowColor = useColorModeValue("#3A56D4", "#8FA2FF");
  const eyebrowBg = useColorModeValue("rgba(58,86,212,0.08)", "rgba(143,162,255,0.12)");
  const eyebrowBorder = useColorModeValue("rgba(58,86,212,0.2)", "rgba(143,162,255,0.25)");
  const sectionWhite = useColorModeValue("white", "gray.900");
  const sectionAlt = useColorModeValue("gray.50", "gray.900");

  const MotionFlex = motion(Flex);

  const SectionHeading = ({ eyebrow, title, subtitle }) => (
    <VStack spacing={3} mb={{ base: 10, md: 12 }} textAlign="center">
      <Text
        fontSize={{ base: "xs", sm: "sm" }}
        fontWeight="700"
        letterSpacing="0.2em"
        textTransform="uppercase"
        color={eyebrowColor}
        bg={eyebrowBg}
        border="1px solid"
        borderColor={eyebrowBorder}
        borderRadius="full"
        px={4}
        py={1.5}
      >
        {eyebrow}
      </Text>
      <Heading
        as="h2"
        size={{ base: "lg", md: "xl" }}
        color={headingColor}
        fontWeight="800"
        letterSpacing="-0.02em"
        lineHeight="1.2"
      >
        {title}
      </Heading>
      {subtitle && (
        <Text fontSize={{ base: "sm", md: "md" }} color={mutedText} maxW="720px" mx="auto" lineHeight="1.8">
          {subtitle}
        </Text>
      )}
    </VStack>
  );

  return (
    <>
      <MetaTags
        title="About Aptofy - Revolutionizing Recruitment with AI-Powered Solutions"
        description="Learn about Aptofy, a leader in AI-driven recruitment solutions. Discover how we're transforming the hiring process with advanced technology, streamlined processes, and comprehensive assessments."
        url="https://www.aptofy.com/AboutUs"
      />
      
      {/* To show different chat bot for loged user and guest user */}
      {userType === '"candidate"' || userType === '"recruiter"' ? (
        <Customerservicebot />
      ) : (
        <Chatbot />
      )}

      <Box bg={sectionWhite} minH="100vh">
        {/* Hero Section */}
        <Box
          position="relative"
          overflow="hidden"
          bg={heroBg}
          borderBottom="1px solid"
          borderColor={eyebrowBorder}
          py={{ base: 12, md: 16 }}
          px={{ base: 4, md: 6 }}
          _before={{
            content: '""',
            position: "absolute",
            top: "-140px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "680px",
            height: "680px",
            borderRadius: "full",
            bg: heroGlow,
            pointerEvents: "none",
          }}
        >
          <Container maxW="900px" position="relative" zIndex={1}>
            <MotionFlex
              direction="column"
              align="center"
              textAlign="center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <Text
                fontSize={{ base: "xs", sm: "sm" }}
                fontWeight="700"
                letterSpacing="0.2em"
                textTransform="uppercase"
                color={eyebrowColor}
                bg={eyebrowBg}
                border="1px solid"
                borderColor={eyebrowBorder}
                borderRadius="full"
                px={4}
                py={1.5}
                mb={5}
              >
                About Aptofy
              </Text>
              <Heading
                as="h1"
                fontSize={{ base: "3xl", sm: "4xl", md: "5xl" }}
                color={headingColor}
                fontWeight="800"
                mb={{ base: 4, md: 5 }}
                lineHeight="1.1"
                letterSpacing="-0.02em"
              >
                Smarter, fairer hiring{" "}
                <Box as="span" bgGradient="linear(135deg, #3B58D7, #5B73E8, #7C5CE8)" bgClip="text">
                  powered by AI
                </Box>
              </Heading>
              <Text fontSize={{ base: "md", md: "lg" }} color={mutedText} maxW="640px" lineHeight="1.8">
                Aptofy is a patented, AI-driven talent platform built to make recruitment
                fast, fair, and sustainable—empowering organizations and individuals to
                reach their full potential.
              </Text>
            </MotionFlex>
          </Container>
        </Box>

        {/* Vision & Mission Section */}
        <Box bg={sectionWhite} py={{ base: 14, md: 20 }} px={{ base: 4, md: 6 }}>
          <Container maxW="1200px">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 6, md: 8 }} alignItems="stretch">
              {[
                {
                  icon: FaEye,
                  label: "Our Vision",
                  text: "Aptofy envisions being the trusted partner in niche talent acquisition, delivering fair, data-driven, and sustainable hiring solutions that unlock the full potential of organizations and individuals alike.",
                  gradient: "linear(135deg, #3B58D7, #5B73E8)",
                  glow: "rgba(58,86,212,0.35)",
                },
                {
                  icon: FaCompass,
                  label: "Our Mission",
                  text: "Aptofy empowers organizations with AI-driven tools that ensure fast, fair, and precise niche talent acquisition for lasting growth.",
                  gradient: "linear(135deg, #16A34A, #22C55E)",
                  glow: "rgba(22,163,74,0.35)",
                },
              ].map((item, i) => (
                <MotionBox
                  key={item.label}
                  display="flex"
                  flexDirection="column"
                  h="100%"
                  p={{ base: 7, md: 9 }}
                  borderRadius="2xl"
                  bg={cardBg}
                  border="1px solid"
                  borderColor={cardBorder}
                  boxShadow={useColorModeValue("0 18px 45px rgba(21,34,84,0.08)", "xl")}
                  position="relative"
                  overflow="hidden"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: i * 0.15 }}
                  viewport={{ once: true }}
                  _hover={{ transform: { base: "none", md: "translateY(-6px)" }, boxShadow: "2xl" }}
                >
                  <Flex
                    align="center"
                    justify="center"
                    w={14}
                    h={14}
                    borderRadius="2xl"
                    mb={5}
                    bgGradient={item.gradient}
                    color="white"
                    boxShadow={`0 10px 24px ${item.glow}`}
                  >
                    <Icon as={item.icon} boxSize={6} />
                  </Flex>
                  <Heading as="h2" size={{ base: "md", md: "lg" }} color={headingColor} fontWeight="800" mb={4}>
                    {item.label}
                  </Heading>
                  <Text fontSize={{ base: "md", md: "lg" }} color={mutedText} lineHeight="1.8" fontStyle="italic">
                    &ldquo;{item.text}&rdquo;
                  </Text>
                </MotionBox>
              ))}
            </SimpleGrid>
          </Container>
        </Box>

        {/* The Aptofy Advantage Section */}
        <Box bg={sectionAlt} py={{ base: 14, md: 20 }} px={{ base: 4, md: 6 }}>
          <Container maxW="1200px">
            <SectionHeading
              eyebrow="Why Aptofy"
              title="The Aptofy Advantage"
              subtitle="A complete, AI-powered hiring engine designed to save time, cut costs, and keep every evaluation fair."
            />
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 6, md: 8 }} alignItems="stretch">
              {advantages.map((adv, i) => (
                <MotionFlex
                  key={adv.title}
                  direction="column"
                  h="100%"
                  p={{ base: 6, md: 8 }}
                  borderRadius="2xl"
                  bg={cardBg}
                  border="1px solid"
                  borderColor={cardBorder}
                  boxShadow={useColorModeValue("0 18px 45px rgba(21,34,84,0.08)", "lg")}
                  position="relative"
                  overflow="hidden"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  _before={{
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "5px",
                    bgGradient: adv.gradient,
                  }}
                  _hover={{
                    transform: { base: "none", md: "translateY(-6px)" },
                    boxShadow: `0 28px 60px ${adv.soft.replace("0.10", "0.28")}`,
                    borderColor: adv.accent,
                  }}
                  transition="all 0.3s ease"
                >
                  <Flex align="center" gap={4} mb={5}>
                    <Center
                      flexShrink={0}
                      w={{ base: "72px", md: "84px" }}
                      h={{ base: "72px", md: "84px" }}
                      borderRadius="2xl"
                      bg={adv.soft}
                    >
                      <Image src={adv.img} alt={adv.title} width="56px" height="56px" />
                    </Center>
                    <Heading
                      as="h3"
                      size={{ base: "sm", md: "md" }}
                      fontWeight="700"
                      color={headingColor}
                      lineHeight="1.35"
                    >
                      {adv.title}
                    </Heading>
                  </Flex>
                  <Text color={mutedText} fontSize={{ base: "sm", md: "md" }} lineHeight="1.8">
                    {adv.desc}
                  </Text>
                </MotionFlex>
              ))}
            </SimpleGrid>
          </Container>
        </Box>

        {/* The Aptofy Values Section */}
        <Box bg={sectionWhite} py={{ base: 14, md: 20 }} px={{ base: 4, md: 6 }}>
          <Container maxW="1200px">
            <SectionHeading
              eyebrow="What Drives Us"
              title="The Aptofy Values"
              subtitle="The principles that shape every product decision and every client relationship."
            />
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} maxW="1000px" mx="auto">
              {values.map((val, i) => (
                <MotionBox
                  key={i}
                  p={6}
                  borderRadius="2xl"
                  boxShadow={useColorModeValue("0 12px 32px rgba(21,34,84,0.06)", "md")}
                  bg={cardBg}
                  border="1px solid"
                  borderColor={cardBorder}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  _hover={{ boxShadow: "xl", transform: "translateY(-4px)" }}
                >
                  <Flex align="start" gap={4}>
                    <Box
                      p={3}
                      borderRadius="xl"
                      bg={
                        val.color === 'blue.500' ? useColorModeValue("blue.50", "blue.900") :
                        val.color === 'green.500' ? useColorModeValue("green.50", "green.900") :
                        val.color === 'purple.500' ? useColorModeValue("purple.50", "purple.900") :
                        useColorModeValue("orange.50", "orange.900")
                      }
                    >
                      <Icon as={val.icon} boxSize={6} color={val.color} />
                    </Box>
                    <Box flex="1">
                      <Heading as="h3" size="md" color={headingColor} mb={2} fontWeight="700">
                        {val.title}
                      </Heading>
                      <Text color={mutedText} fontSize="sm" lineHeight="1.7">
                        {val.desc}
                      </Text>
                    </Box>
                  </Flex>
                </MotionBox>
              ))}
            </SimpleGrid>
          </Container>
        </Box>

        {/* The Aptofy Process Section */}
        <Box bg={sectionAlt} py={{ base: 14, md: 20 }} px={{ base: 4, md: 6 }}>
          <Container maxW="1200px">
            <SectionHeading
              eyebrow="How It Works"
              title="The Aptofy Process"
              subtitle="A streamlined, end-to-end flow from sourcing to selection."
            />
            <Flex justify="center">
              <Box
                w="full"
                maxW="900px"
                borderRadius="2xl"
                overflow="hidden"
                boxShadow="2xl"
                border="1px solid"
                borderColor={cardBorder}
                bg={cardBg}
              >
                <Image src={ImageFlowchart} alt="Aptofy Process Flowchart" />
              </Box>
            </Flex>
          </Container>
        </Box>

        {/* The Aptofy IP Section */}
        <Box bg={sectionWhite} py={{ base: 14, md: 20 }} px={{ base: 4, md: 6 }}>
          <Container maxW="1200px">
            <SectionHeading
              eyebrow="Recognized Innovation"
              title="The Aptofy IP"
              subtitle="Our technology is patented for its impact on fair hiring and a greener planet."
            />
            <MotionBox
              p={{ base: 6, md: 10 }}
              borderRadius="2xl"
              boxShadow="2xl"
              border="1px solid"
              borderColor={cardBorder}
              bg={cardBg}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <VStack spacing={6} textAlign="center">
                <Flex
                  align="center"
                  justify="center"
                  w={16}
                  h={16}
                  borderRadius="2xl"
                  bgGradient="linear(135deg, #D69E2E, #F2C75C)"
                  color="white"
                  boxShadow="0 10px 24px rgba(214,158,46,0.4)"
                >
                  <Icon as={FaAward} boxSize={8} />
                </Flex>
                <Heading as="h3" size={{ base: "md", md: "lg" }} color={headingColor} fontWeight="700">
                  Granted by The Patent Office, Govt. of India
                </Heading>
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color={eyebrowColor}
                  fontWeight="700"
                  px={4}
                  py={2}
                  borderRadius="full"
                  bg={eyebrowBg}
                  border="1px solid"
                  borderColor={eyebrowBorder}
                >
                  Patent No: 569726
                </Text>
                <Text fontSize={{ base: "sm", md: "md" }} color={mutedText} maxW="900px" lineHeight="1.7" fontStyle="italic">
                  &ldquo;System and Method for Conducting Interviews with Real-Time Scoring and Reducing Carbon Emission&rdquo;
                </Text>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} w="full" maxW="640px" pt={2}>
                  {[
                    { icon: FaRocket, color: "blue.500", title: "Innovation", text: "AI-powered recruitment technology" },
                    { icon: FaLeaf, color: "green.500", title: "Sustainability", text: "Reducing carbon emissions" },
                    { icon: FaBullseye, color: "purple.500", title: "Efficiency", text: "Faster, fairer hiring" },
                  ].map((p) => (
                    <VStack key={p.title} spacing={2}>
                      <Icon as={p.icon} boxSize={8} color={p.color} />
                      <Text fontWeight="700" color={headingColor}>
                        {p.title}
                      </Text>
                      <Text fontSize="sm" color={mutedText} textAlign="center">
                        {p.text}
                      </Text>
                    </VStack>
                  ))}
                </SimpleGrid>
              </VStack>
            </MotionBox>
          </Container>
        </Box>

        {/* Carbon Emission Calculator Section */}
        <Box bg={sectionAlt} py={{ base: 14, md: 20 }} px={{ base: 4, md: 6 }}>
          <Container maxW="1200px">
            <MotionBox
              p={{ base: 4, md: 6 }}
              borderRadius="2xl"
              boxShadow="2xl"
              border="1px solid"
              borderColor={cardBorder}
              bg={cardBg}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <CarbonEmissionCalculator />
            </MotionBox>
          </Container>
        </Box>

        {/* Final CTA Section */}
        <Box bg={sectionWhite} py={{ base: 14, md: 20 }} px={{ base: 4, md: 6 }}>
          <Container maxW="1100px">
            <MotionBox
              p={{ base: 8, md: 14 }}
              borderRadius="3xl"
              position="relative"
              overflow="hidden"
              bgGradient="linear(135deg, #3B58D7, #5B73E8 55%, #7C5CE8)"
              boxShadow="0 30px 70px rgba(58,86,212,0.35)"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <VStack spacing={5} textAlign="center" color="white">
                <Heading as="h2" size={{ base: "lg", md: "xl" }} fontWeight="800" letterSpacing="-0.02em">
                  Ready to transform the way you hire?
                </Heading>
                <Text fontSize={{ base: "md", md: "lg" }} maxW="640px" lineHeight="1.8" opacity={0.95}>
                  Join the organizations building fair, future-ready talent pipelines with Aptofy.
                </Text>
                <Link href="/career" passHref>
                  <MotionButton
                    bg="white"
                    color="#3B58D7"
                    size="lg"
                    px={10}
                    py={7}
                    fontSize="md"
                    fontWeight="700"
                    borderRadius="full"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    viewport={{ once: true }}
                    _hover={{ transform: "translateY(-3px)", boxShadow: "2xl", bg: "gray.50" }}
                  >
                    Join Us Today
                  </MotionButton>
                </Link>
              </VStack>
            </MotionBox>
          </Container>
        </Box>

        {/* Footer */}
        <Footer />
      </Box>
    </>
  );
};

export default AboutPage;